


















import { computed, defineComponent, getCurrentInstance, onMounted, onUnmounted } from "@vue/composition-api";
import { EVENT_NAMES } from "@scrile/api-provider/dist";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useOnUserLogin from "@/hooks/useOnUserLogin";
import useSystemSettings from "@/hooks/useSystemSettings";
import useWatchMarkReadMessage from "@/hooks/useWatchMarkReadMessage";
import { ACTION_TYPES } from "@/store/const";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { base64UrlToUint8Array } from "@/lib/encryptionHelper";
import { isGetUserMediaSupported, isWebRTCSupported } from "@/lib/webrtcHelpers";
import config from "@/config";
import BalanceManager from "@/components/BalanceManager";
import CallController from "@/components/CallController";
import CreatePost from "@/components/CreatePost";
import IosPushNotificationReminder from "@/components/IosPushNotificationReminder";
import LoginSignUpModal from "@/components/LoginSignUpModal";
import MainNavigationView from "@/components/MainNavigationView";
import ModalFollowSubscribe from "@/components/ModalFollowSubscribe";
import ModalRefillBalance from "@/components/ModalRefillBalance";
import NotificationBar from "@/components/NotificationBar";
import PurchaseManager from "@/components/PurchaseManager";
import PushManager from "@/components/PushManager";
import StatusNotificationBar from "@/components/StatusNotificationBar";
import "@/lib/audioPlayer";
import "./styles/app.scss";
import "./styles/icons.scss";

document.title = config.siteName;

export default defineComponent({
  name: "App",
  components: {
    ModalFollowSubscribe,
    PushManager,
    PurchaseManager,
    CreatePost,
    StatusNotificationBar,
    NotificationBar,
    MainNavigationView,
    BalanceManager,
    ModalRefillBalance,
    CallController,
    LoginSignUpModal,
    IosPushNotificationReminder,
  },
  setup() {
    const { getData } = useSystemSettings();
    const { authUser } = useAuthUser();
    const vm = getCurrentInstance();
    useWatchMarkReadMessage();
    getData();

    const setUserHandler = (payload: { user: User | null; authorities: string[] | null } | null) => {
      if (payload === null || payload.user === null) {
        if (authUser.value !== null) vm?.$router.push("/logout");
      } else {
        vm?.$store.dispatch(ACTION_TYPES.LOGIN, payload);
      }
    };

    const featureCalling = computed(() => {
      return config.featureCalling && isWebRTCSupported() && isGetUserMediaSupported();
    });

    const initPushRequest = async () => {
      if (Notification.permission === "denied") {
        return;
      }
      if (Notification.permission === "default") {
        const requestedPermission = await Notification.requestPermission();
        if (requestedPermission !== "granted") {
          return;
        }
      }
      const reg = await navigator.serviceWorker.ready;
      const currentSubscription = await reg.pushManager.getSubscription();
      if (currentSubscription !== null) {
        return;
      }
      const unit8Buffer = base64UrlToUint8Array(config.webPushNotificationsPublicKey);
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: unit8Buffer,
      });
      const subscriptionsJson = subscription.toJSON();
      await providers.PushNotificationsProvider.webPushNotificationsSubscribe({
        data: {
          endpoint: subscriptionsJson.endpoint ?? "",
          p256dh: subscriptionsJson.keys?.p256dh ?? "",
          auth: subscriptionsJson.keys?.auth ?? "",
        },
      });
    };

    useOnUserLogin(() => {
      if (config.webPushNotificationsPublicKey && "serviceWorker" in navigator) {
        initPushRequest();
      }
    });

    emitter.on<any>(EVENT_NAMES.SET_USER, setUserHandler);
    onMounted(() => {
      if (config.webPushNotificationsPublicKey && "serviceWorker" in navigator && authUser.value) {
        window.addEventListener(
          "click",
          () => {
            initPushRequest();
          },
          { once: true }
        );
      }
    });
    onUnmounted(() => emitter.off<any>(EVENT_NAMES.SET_USER, setUserHandler));

    return {
      featureCalling,
      authUser,
    };
  },
});
